import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * The two unread counts the shell shows, as two pollers that never mix.
 *
 * **Why two.** Until T14-M2 there was one, `/notifications/unread-count`, and
 * both badges read it — the bell's and the Messages cell's on the mobile bar.
 * That is right for the bell, which is what a notification is, and wrong for
 * the cell: a quote arriving or a job being accepted lit "Mensajes", and the
 * one act that cell describes — opening the conversation — could not put it
 * out, because the rows holding it up were never about messages. The counter
 * went up and never came down. Jorge's call on 2026-09-22: separate channels.
 *
 * So the bell counts notifications and the cell counts unread MESSAGES, each
 * from the endpoint that means it.
 *
 * **Why a module-level store and not state inside the hook.** Jorge's rule from
 * T12-M1, and it still holds: two components polling the same URL on their own
 * 45s timers are two requests and two answers that disagree for up to 45
 * seconds. Each channel owns exactly one request however many components
 * subscribe, and the last subscriber to leave stops the timer.
 *
 * A channel also wakes on a DOM event, because the server sometimes changes
 * under a page that already knows it did — opening a thread marks both its
 * messages and its bell rows read, and without the event each badge would carry
 * them until the next tick.
 */
const POLL_MS = 45000;

function createChannel({ path, event }) {
  const channel = {
    path,
    event,
    count: 0,
    timer: null,
    listeners: new Set(),
  };

  channel.emit = () => channel.listeners.forEach((fn) => fn(channel.count));

  channel.load = async () => {
    try {
      const { data } = await api.get(path);
      const next = data.unread || 0;
      if (next !== channel.count) {
        channel.count = next;
        channel.emit();
      }
    } catch {
      // A failed poll keeps the last known count. A badge that blinks to zero
      // on a dropped request is worse than a badge that is briefly stale.
    }
  };

  channel.start = () => {
    if (channel.timer) return;
    channel.load();
    channel.timer = setInterval(channel.load, POLL_MS);
    window.addEventListener(event, channel.load);
  };

  channel.stop = () => {
    if (!channel.timer) return;
    clearInterval(channel.timer);
    channel.timer = null;
    window.removeEventListener(event, channel.load);
  };

  /** Lets a component that already knows better (the bell, after "mark all
   *  read") correct the shared value without waiting for the next poll. */
  channel.set = (next) => {
    channel.count = Math.max(0, next || 0);
    channel.emit();
  };

  return channel;
}

/** The bell. Every unread notification row, of every type. */
const NOTIFICATIONS = createChannel({
  path: "/notifications/unread-count",
  event: "fixy:notifications-changed",
});

/** The Messages cell of the mobile bar. Unread messages in threads, and
 *  nothing else — see `unread_count` in backend/routes/chat.py. */
const MESSAGES = createChannel({
  path: "/chat/unread-count",
  event: "fixy:messages-changed",
});

/** Subscribe to one channel. The generic both hooks below are made of. */
function useChannel(channel) {
  const { user } = useAuth();
  const [value, setValue] = useState(channel.count);

  useEffect(() => {
    // No session, no counts — and no request either. Both endpoints are
    // authenticated, so polling them anonymously is a 401 every 45 seconds.
    if (!user) {
      channel.count = 0;
      setValue(0);
      return undefined;
    }
    channel.listeners.add(setValue);
    setValue(channel.count);
    channel.start();
    return () => {
      channel.listeners.delete(setValue);
      if (channel.listeners.size === 0) channel.stop();
    };
  }, [channel, user]);

  return value;
}

export function useUnreadCount() {
  return useChannel(NOTIFICATIONS);
}

export function useUnreadMessages() {
  return useChannel(MESSAGES);
}

export const setUnreadCount = NOTIFICATIONS.set;
export const setUnreadMessages = MESSAGES.set;

export default useUnreadCount;
